import { useQuery } from "@tanstack/react-query";
import { Users, Calendar, TrendingUp } from "lucide-react";
import {
  VentureCard,
  VentureCardHeader,
  VentureCardTitle,
  VentureCardContent,
} from "@/components/ui/venture-card";
import { supabase } from "@/integrations/supabase/client";

function startOfIsoWeek(): string {
  const now = new Date();
  const dow = now.getUTCDay();
  const diff = (dow + 6) % 7;
  const d = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - diff)
  );
  return d.toISOString();
}

export default function Home() {
  const weekStart = startOfIsoWeek();

  // My vh_team_members row — identity anchor for setter scoping.
  const { data: vhMember } = useQuery({
    queryKey: ["my-vh-team-member-row"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;
      const { data } = await supabase
        .from("vh_team_members")
        .select("id, name")
        .eq("user_id", user.id)
        .eq("role", "setter")
        .maybeSingle();
      return data;
    },
    staleTime: 60_000,
  });

  const setterId = vhMember?.id;

  // Leads added this week (funnel_events any row for this setter with lead_created_at in week)
  const { data: leadsCount } = useQuery({
    queryKey: ["setter-home-leads", setterId, weekStart],
    enabled: !!setterId,
    queryFn: async () => {
      const { count, error } = await supabase
        .from("funnel_events")
        .select("id", { count: "exact", head: true })
        .eq("setter_id", setterId!)
        .gte("lead_created_at", weekStart);
      if (error) throw error;
      return count ?? 0;
    },
    staleTime: 60_000,
  });

  // Demos booked (stage=demo_booked) — count distinct funnel events with demo_booked_at this week.
  const { data: bookedCount } = useQuery({
    queryKey: ["setter-home-booked", setterId, weekStart],
    enabled: !!setterId,
    queryFn: async () => {
      const { count, error } = await supabase
        .from("funnel_events")
        .select("id", { count: "exact", head: true })
        .eq("setter_id", setterId!)
        .gte("demo_booked_at", weekStart);
      if (error) throw error;
      return count ?? 0;
    },
    staleTime: 60_000,
  });

  // Demos showed (held) this week
  const { data: showedCount } = useQuery({
    queryKey: ["setter-home-showed", setterId, weekStart],
    enabled: !!setterId,
    queryFn: async () => {
      const { count, error } = await supabase
        .from("funnel_events")
        .select("id", { count: "exact", head: true })
        .eq("setter_id", setterId!)
        .gte("demo_showed_at", weekStart);
      if (error) throw error;
      return count ?? 0;
    },
    staleTime: 60_000,
  });

  const showRate =
    bookedCount && bookedCount > 0 && showedCount !== undefined
      ? Math.round((showedCount / bookedCount) * 100)
      : 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Setter Home</h1>
        <p className="text-muted-foreground">Your pipeline this week.</p>
      </div>

      {!vhMember && (
        <VentureCard>
          <VentureCardContent>
            <p className="text-sm text-muted-foreground">
              No setter profile linked to your account yet. An admin must link
              your team member record before your stats appear.
            </p>
          </VentureCardContent>
        </VentureCard>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <VentureCard>
          <VentureCardHeader>
            <VentureCardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-secondary" />
              Leads (this week)
            </VentureCardTitle>
          </VentureCardHeader>
          <VentureCardContent>
            <p className="text-3xl font-bold">{leadsCount ?? 0}</p>
          </VentureCardContent>
        </VentureCard>

        <VentureCard>
          <VentureCardHeader>
            <VentureCardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-500" />
              Demos Booked (this week)
            </VentureCardTitle>
          </VentureCardHeader>
          <VentureCardContent>
            <p className="text-3xl font-bold">{bookedCount ?? 0}</p>
          </VentureCardContent>
        </VentureCard>

        <VentureCard>
          <VentureCardHeader>
            <VentureCardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-500" />
              Show Rate
            </VentureCardTitle>
          </VentureCardHeader>
          <VentureCardContent>
            <p className="text-3xl font-bold">{showRate}%</p>
            <p className="text-xs text-muted-foreground">
              {showedCount ?? 0} / {bookedCount ?? 0}
            </p>
          </VentureCardContent>
        </VentureCard>
      </div>
    </div>
  );
}
